import "@/App.css";
import "leaflet/dist/leaflet.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
// Navigator pulls in the heavy map libraries (mapbox-gl / leaflet). Loading it
// lazily keeps that chunk off the lightweight content/SEO pages (better CWV).
const Navigator = lazy(() => import("@/pages/Navigator"));
import About from "@/pages/About";
import CityPage from "@/pages/CityPage";
import BlogIndex from "@/pages/BlogIndex";
import BlogArticle from "@/pages/BlogArticle";
import ThematicPage from "@/pages/ThematicPage";
import ModelPage from "@/pages/ModelPage";
import DepartmentPage from "@/pages/DepartmentPage";
import TripPage from "@/pages/TripPage";
import CompareModels from "@/pages/CompareModels";
import ChargingPage from "@/pages/ChargingPage";
import RegionPage from "@/pages/RegionPage";
import Account from "@/pages/Account";
import Premium from "@/pages/Premium";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/components/AuthCallback";
import Legal from "@/pages/Legal";
import { CookieConsent } from "@/components/CookieConsent";
import { TrialEndingBanner } from "@/components/TrialEndingBanner";
import { PremiumProvider, usePremium } from "@/context/PremiumContext";
import { THEMATIC_PAGES } from "@/lib/thematic";
import { MODEL_PATH } from "@/lib/models";
import { DEPT_PATH } from "@/lib/departments";
import { TRIP_PATH } from "@/lib/trips";
import { CHARGING_PATH } from "@/lib/cityCoords";
import { REGION_PATH } from "@/lib/regions";
import { SeoFooter } from "@/components/SeoFooter";
import { PartnerBanner } from "@/components/PartnerBanner";
import { Toaster } from "@/components/ui/sonner";
import { initTheme } from "@/lib/theme";
import { AutoNightTheme } from "@/components/AutoNightTheme";
import { fetchRatingSummary } from "@/lib/api";

initTheme();

// Inject AggregateRating structured data ONLY from genuine, aggregated user
// ratings (never fabricated). Rendered client-side; Google executes JS.
function RatingSeo() {
  useEffect(() => {
    let cancelled = false;
    fetchRatingSummary().then((s) => {
      if (cancelled || !s || !s.count || s.count < 1) return;
      const data = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "vspo",
        applicationCategory: "TravelApplication",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: String(s.average),
          reviewCount: String(s.count),
          bestRating: "5",
          worstRating: "1",
        },
      };
      let el = document.getElementById("rating-jsonld");
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = "rating-jsonld";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    });
    return () => { cancelled = true; };
  }, []);
  return null;
}

function App() {
  return (
    <PremiumProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </PremiumProvider>
  );
}

function AppShell() {
  const location = useLocation();
  const { isPremium } = usePremium() || {};
  // Returning from OAuth: process the session before anything else.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App flex h-screen flex-col overflow-hidden">
      <RatingSeo />
      <AutoNightTheme />
      {!isPremium && <PartnerBanner position="top" />}
      <TrialEndingBanner />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Routes>
          <Route
            path="/"
            element={
              <div className="h-full overflow-y-auto">
                <Suspense fallback={<div className="flex h-screen items-center justify-center bg-background text-sm text-muted-foreground">Chargement du GPS…</div>}>
                  <Navigator />
                </Suspense>
                <SeoFooter variant="app" />
              </div>
            }
          />
          <Route path="/a-propos" element={<About />} />
          <Route path="/gps-voiture-sans-permis/:slug" element={<CityPage />} />
          <Route path="/conseils" element={<BlogIndex />} />
          <Route path="/conseils/:slug" element={<BlogArticle />} />
          {THEMATIC_PAGES.map((t) => (
            <Route key={t.slug} path={t.path} element={<ThematicPage slug={t.slug} />} />
          ))}
          <Route path={`${DEPT_PATH}/:slug`} element={<DepartmentPage />} />
          <Route path={`${MODEL_PATH}/:slug`} element={<ModelPage />} />
          <Route path={`${TRIP_PATH}/:slug`} element={<TripPage />} />
          <Route path={`${CHARGING_PATH}/:slug`} element={<ChargingPage />} />
          <Route path={`${REGION_PATH}/:slug`} element={<RegionPage />} />
          <Route path="/comparatif-voiture-sans-permis" element={<CompareModels />} />
          <Route path="/compte" element={<Account />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/confidentialite" element={<Legal variant="privacy" />} />
          <Route path="/mentions-legales" element={<Legal variant="legal" />} />
        </Routes>
      </div>
      {!isPremium && <PartnerBanner position="bottom" />}
      <CookieConsent />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
